import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { EditDeleteCellRenderer, toast, UserProfileCellRenderer } from '../../../components';
import { store } from '../../../redux';
import { CustomApi } from '../../../services';
import { formatPhoneNumber, getFormattedBool, getFormattedDateTime } from '../../../utils';
import { ROLES } from '../../userMgmt/utils';

const MySwal = withReactContent(Swal);

export const adminUsersColumnDefs = (appIdSelected) => [
  {
    headerName: 'Email',
    field: 'userName',
    suppressMovable: true,
    cellRenderer: UserProfileCellRenderer,
    cellRendererParams(params) {
      const { data, api, node } = params;
      const lockOutExpireDate = getFormattedDateTime(data.lockoutEnd);
      const phoneNumber = formatPhoneNumber(data.phoneNumber);
      const confirmedEmail = getFormattedBool(data.emailConfirmed);
      return {
        data,
        lockOutExpireDate,
        phoneNumber,
        confirmedEmail,
        api,
        node,
        onClickEdit() {
          let rowIndex = 0;
          api.forEachNodeAfterFilterAndSort((node, nodeIndex) => {
            if (node.data.userId === data.userId) rowIndex = nodeIndex;
          });
          api.startEditingCell({ colKey: 'role', rowIndex: rowIndex });
        },
        onClickCancelEdit() {
          api.stopEditing(true);
        },
        async onClickDelete() {
          let userResponsePromise = new Promise((resolve, reject) => {
            let textContent = document.createElement('div');

            if (data.userName.length >= 30) {
              textContent.innerHTML = `<b><div class="user-name" style="font-size: 20px;">${data.userName}?</div>Are you sure you want to delete this User?</b>`;
            } else {
              textContent.innerHTML = `<b><div class="user-name" style="font-size: 27px;">${data.userName}?</div>Are you sure you want to delete this User?</b>`;
            }

            MySwal.fire({
              allowOutsideClick: false,
              title: 'Are you sure?',
              text: 'This User will be deleted.',
              icon: 'warning',
              showCancelButton: true,
              cancelButtonText: 'No',
              confirmButtonText: 'Yes',
            })
              .then((submit) => {
                resolve(submit.isConfirmed);
              })
              .catch((err) => {
                reject(err);
              });
          });

          let userResponse;

          try {
            userResponse = await userResponsePromise;
          } catch (error) {
            userResponse = false;
            console.log(error);
          }

          if (userResponse) {
            try {
              await Api.delete(`/users/${data.userId}`);
              api.gridBodyCtrl.gridOptionsService.gridOptions.rowData.splice(node.rowIndex, 1);
              api.setRowData(api.gridBodyCtrl.gridOptionsService.gridOptions.rowData);
              toast.success(`Successfully deleted User ${data.userName}!`);
            } catch (error) {
              toast.error(`Failed to delete User ${data.userName}.`);
            }
          }
        },
        async onClickConfirmEdit() {
          try {
            const model = {
              sonarUserId: data.userId,
              role: data.userRole,
            };
            await Api.post('/users/assign-role', model);
            toast.success(`Successfully updated User ${data.userName}!`);
          } catch (error) {
            toast.error(`Failed to update User ${data.userName}.`);
          }
        },
      };
    },
  },
  {
    headerName: 'Email Confirmed',
    field: 'emailConfirmed',
    cellRenderer: (params) => {
      return params.value === true ? 'Yes' : 'No';
    },
  },
  {
    headerName: 'Phone',
    field: 'phoneNumber',
    valueFormatter: ({ value }) => formatPhoneNumber(value),
  },
  {
    headerName: 'Lock Out Expiration',
    field: 'lockoutEnd',
    valueFormatter: ({ value }) => getFormattedDateTime(value),
  },
  {
    headerName: 'Role',
    field: 'userRole',
    colId: 'role',
    cellRenderer: (params) => {
      // If userRole is null and ssoAccount is true, display "SSO"
      if (params.data?.userRole === null && params.data?.ssoAccount) {
        return 'SSO';
      }
      return params.value; // Default behavior (show role value)
    },
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: typeof ROLES === 'object' && 'all' in ROLES ? ROLES.all : ROLES,
    },
    editable(params) {
      const { node } = params;
      const { editDeleteCR } = store.getState()?.grids || {};
      return node.data.userId === editDeleteCR.id;
    },
  },
  {
    headerName: 'Edit/Delete',
    colId: 'editDelete',
    suppressMovable: true,
    sortable: false,
    width: 100,
    cellRenderer: EditDeleteCellRenderer,
    cellRendererParams(params) {
      const { data, api, node } = params;
      const { userId: id, ssoAccount, userRole } = data;

      // Get current user's role from Redux store
      const currentUserRole = store.getState()?.auth?.user?.role;
      const isSuperAdmin = userRole === 'SuperAdmin';
      const currentUserIsSuperAdmin = currentUserRole === 'SuperAdmin';

      return {
        id,
        disableDelete: ssoAccount || (!currentUserIsSuperAdmin && isSuperAdmin),
        disableEdit: ssoAccount || (!currentUserIsSuperAdmin && isSuperAdmin),
        onClickEdit() {
          if (ssoAccount || (!currentUserIsSuperAdmin && isSuperAdmin)) return; // Disable edit button for SSO / superAdmin edit for non superAdmin roles
          let rowIndex = 0;
          api.forEachNodeAfterFilterAndSort((node, nodeIndex) => {
            if (node.data.userId === id) {
              rowIndex = nodeIndex;
            }
          });
          api.startEditingCell({ colKey: 'role', rowIndex: rowIndex });
        },
        onClickCancelEdit() {
          api.stopEditing(true);
        },
        async onClickDelete() {
          let userResponsePromise = new Promise((resolve, reject) => {
            MySwal.fire({
              allowOutsideClick: false,
              title: 'Are you sure?',
              text: 'This User will be deleted.',
              icon: 'warning',
              showCancelButton: true,
              cancelButtonText: 'No',
              confirmButtonText: 'Yes',
            })
              .then((submit) => {
                resolve(submit.isConfirmed);
              })
              .catch((err) => {
                reject(err);
              });
          });

          let userResponse;

          try {
            userResponse = await userResponsePromise;
          } catch (error) {
            userResponse = false;
            console.log(error);
          }

          if (userResponse) {
            try {
              await CustomApi.delete(`api/applications/${appIdSelected}/users/${data.userId}`);
              api.gridBodyCtrl.gridOptionsService.gridOptions.rowData.splice(node.rowIndex, 1);
              api.setRowData(api.gridBodyCtrl.gridOptionsService.gridOptions.rowData);
              toast.success(`Successfully deleted User ${data.userName}!`);
            } catch (error) {
              toast.error(`Failed to delete User ${data.userName}.`);
            }
          }
        },
        async onClickConfirmEdit() {
          try {
            const model = {
              sonarUserId: data.userId,
              role: data.userRole,
            };
            await CustomApi.post(`api/users/assign-role?applicationId=${appIdSelected}`, model);
            toast.success(`Successfully updated User ${data.userName}!`);
          } catch (error) {
            toast.error(`Failed to update User ${data.userName}.`);
          }
        },
      };
    },
  },
];
